/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import TextLinkButton from '../../components/shared/TextLinkButton'
import DocIcon from '../../components/shared/DocIcon'
import { AclLevel, FsiType, SystemFolderType } from '../../constants/fsi'
import DocState from '../../components/shared/DocState'
import ValidDocIndicator from '../../components/shared/ValidDocIndicator'
import DueDateIndicator from '../../components/DueDateIndicator'
import { DOCUMENTS_VIEW_TYPE, DocumentsViewType } from './types'
import useDocumentsListService from '../../hooks/useDocumentsListService'
import usePropertySearchService from '../../hooks/usePropertySearchService'
import { FsiGridItem } from './FsiGridItem'
import { DocumentActionMenu } from '../../components/home/sections/recent-docs/DocumentActionMenu'
import ReportsTableMenu from './ReportsTableMenu'
import { createDateTimeFormatter } from '../../utils/commonColumns'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionContext,
  TableMultiSelectionConsumer
} from '../../components/rcm-table/TableMultiSelection'
import { useModal } from '~/contexts/ModalContext'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import MultipleWorkflowsModal from '~/components/modals/MultipleWorkflowsModal'
import { WrapperOverlay } from './quality-issues'
import { usePolling } from '~/hooks/usePolling'

interface DocumentsTableProps {
  fsi: any
  isPropertySearch?: boolean
  viewType: DocumentsViewType
  initialPage?: number
  filterShown?: boolean
  hideVersion?: boolean
  onNavigateToFolder?: (folderId: number) => void
  setWorkFlowModalDetails?: (value: { isModalVisible: boolean; id: null | number }) => void
}

export const DocumentsTable: React.FC<DocumentsTableProps> = (props) => {
  const { fsi, isPropertySearch, viewType } = props
  const context = useServiceContext()
  const { access } = context.service
  const { assembleRegularFolderFiltersFromFormValues } = useDocumentsListService()
  const { assembleFiltersFromFormValues: assemblePropertySearchFiltersFromFormValues } = usePropertySearchService()

  const canShowMultiSelect = () => {
    return (
      access.isAllowed('documentsSelectMultiFiles') &&
      ((viewType as any) === SystemFolderType.REGULATED_CONTENT ||
        isPropertySearch ||
        ((viewType as any) === SystemFolderType.TEMPLATES &&
          fsi.uuid !== 'FOLDER_STRUCTURE' &&
          fsi.isFolderStructureSubFolder))
    )
  }

  // If multi-select is not enabled, render table directly without wrapper
  if (!canShowMultiSelect()) {
    return <DocumentsTableContent {...props} />
  }

  // Wrap with multi-selection functionality
  return (
    <TableMultiSelectionWrapper
      texts={{
        selectAllItemsText: isPropertySearch
          ? 'PROPERTIESSEARCH_MULTISELECT_SELECT_ALL_IN_SECTION'
          : 'DOCUMENTS_MULTISELECT_SELECT_ALL_IN_SECTION',
        allItemsSelectedText: isPropertySearch
          ? 'PROPERTIESSEARCH_MULTISELECT_ALL_N_ITEMS_SELECTED'
          : 'DOCUMENTS_MULTISELECT_ALL_N_ITEMS_SELECTED'
      }}
      textParams={{
        folderPathText: fsi.folderPathText
      }}
      getTotalNumberFunction={async (filters: any) => {
        if (isPropertySearch) {
          const result = await context.api.files.propertySearchGetCount({ ...filters })
          // propertySearchGetCount returns either {anyFilterActive: false} or a number directly
          if (typeof result === 'object' && 'anyFilterActive' in result && result.anyFilterActive === false) {
            return 0
          }
          // The API returns a number directly
          return typeof result === 'number' ? result : 0
        } else {
          const result = await context.api.files.getNumbersofdocs(fsi.id, { ...filters })
          return result.sumOfDocumentsWithoutWorkflowInFolder || 0
        }
      }}
      assembleFilterFunction={
        isPropertySearch ? assemblePropertySearchFiltersFromFormValues : assembleRegularFolderFiltersFromFormValues
      }
    >
      <DocumentsTableContent {...props} />
    </TableMultiSelectionWrapper>
  )
}

// Inner component that renders the actual table
const DocumentsTableContent: React.FC<DocumentsTableProps> = ({
  fsi,
  isPropertySearch,
  viewType,
  initialPage,
  filterShown,
  hideVersion,
  onNavigateToFolder,
  setWorkFlowModalDetails
}) => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const { getFsiNameTooltip } = useDocumentsListService()
  const { showModal } = useModal()
  const { startPolling } = usePolling()
  const {
    service: {
      filter: { translate },
      access,
      init
    }
  } = context

  const [isSiteNumberShown, setIsSiteNumberShown] = useState(false)
  const [isSubjectShown, setIsSubjectShown] = useState(false)
  const [isDataPageShown, setIsDataPageShown] = useState(false)
  const [expandedRows, setExpandedRows] = useState<number[]>([])
  const [supersededDocListByFsi, setSupersededDocListByFsi] = useState<Record<number, any[]>>({})

  const qcReviewEnabled = init.getFeaturesInfo()?.qcReview?.qcReviewEnabled || false
  const isEtmfEnabled = init.getFeaturesInfo()?.etmfFeature?.show || false

  // Access table constants for permission checks - matches legacy accessService.rightsTable.js GROUPS
  const accessTable = {
    rcmAdmins: ['ADMIN', 'CONTENT_ADMIN'],
    stagingManagers: ['ETMF_MANAGER', 'ETMF_SPECIALIST'],
    rcmUsers: ['POWER_USER', 'USER'],
    stagingUsers: ['ETMF_SITE_USER', 'ETMF_STUDY_USER']
  }

  /**
   * Determines if the quality issue indicator should be shown for a document.
   * Shows a warning icon for documents with quality issues (documentDefective).
   *
   * Migrated from legacy: lego/page/browser/list/Table.jsx shouldShowQualityIssueIndicator()
   */
  const shouldShowQualityIssueIndicator = (parentFsi: any, row: any): boolean => {
    return (
      isEtmfEnabled &&
      access.isAllowed([
        accessTable.rcmAdmins,
        accessTable.stagingManagers,
        accessTable.rcmUsers,
        accessTable.stagingUsers
      ] as any) &&
      [AclLevel.BROWSE, AclLevel.VIEW, AclLevel.READ, AclLevel.WRITE, AclLevel.DELETE].includes(parentFsi?.acl) &&
      row?.documentDefective
    )
  }

  // Determine if sorting should be enabled for this view
  const isSortingEnabled = viewType !== DOCUMENTS_VIEW_TYPE.recent

  useEffect(() => {
    // Listen for table update events via event bus
    const handleUpdateTable = () => {
      tableData.refreshTable()
    }

    const handleUpdateSupersededDocs = () => {
      setSupersededDocListByFsi({})
      setExpandedRows([])
    }

    // Use window event bus for global updates
    window.addEventListener('bctable:updateTable', handleUpdateTable)
    window.addEventListener('updateFiles', handleUpdateTable)
    window.addEventListener('bctable:updateSupersededDocs', handleUpdateSupersededDocs)
    // WebSocket or polling support for real-time updates
    // Matches legacy pattern from Table.jsx (lines 67-81) and browser.controller.js (line 336)

    if (!isPropertySearch) {
      if (context?.config?.websocketsEnabled) {
        // TODO: WebSocket support pending - requires websocket service integration
        console.debug('WebSocket support for document table updates: pending implementation')
      } else {
        // Polling fallback (matches browser.controller.js line 336 pattern)
        startPolling(() => {
          tableData.refreshTable(null, true)
        })
      }
    }

    return () => {
      window.removeEventListener('bctable:updateTable', handleUpdateTable)
      window.removeEventListener('updateFiles', handleUpdateTable)
      window.removeEventListener('bctable:updateSupersededDocs', handleUpdateSupersededDocs)
    }
  }, [tableData, context.bus])

  useEffect(() => {
    // Check if we need to show site number column
    setIsSiteNumberShown(
      fsi?.name === 'Sites' && !['ETMF_SITE_USER', 'ETMF_STUDY_USER'].includes(context.service.auth.getRole() || '')
    )

    // Check supplemental data columns
    const supplementalDataValues = tableData.entries.reduce(
      (memo, file: any) => {
        if (file.supplementalData?.subjectName) {
          memo.isSubjectValuePresent = true
        }
        if (file.supplementalData?.dataPageName) {
          memo.isDataPageValuePresent = true
        }
        return memo
      },
      {
        isSubjectValuePresent: false,
        isDataPageValuePresent: false
      }
    )

    setIsDataPageShown(fsi?.isEDCAppPresent && supplementalDataValues.isDataPageValuePresent)
    setIsSubjectShown(fsi?.isEDCAppPresent && supplementalDataValues.isSubjectValuePresent)
  }, [fsi, tableData.entries])

  useEffect(() => {
    if (
      [DOCUMENTS_VIEW_TYPE.favorites, DOCUMENTS_VIEW_TYPE.recent, DOCUMENTS_VIEW_TYPE.systemDocuments].includes(
        viewType as any
      )
    ) {
      tableData.refreshTable()
    }
  }, [])

  const navigate = useNavigate()
  const handleRowEnter = (row: any) => {
    if (
      !row.valid ||
      (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
      row.type === FsiType.PLACEHOLDER
    ) {
      return
    }

    if (row.isReport) {
      // Download report
      window.open(`/api/files/${row.id}/download`, '_blank')
      return
    }

    if (row.type === FsiType.FOLDER) {
      // Check if this is a system folder (has systemFolderType)
      if (row.systemFolderType) {
        // Navigate with type parameter for system folders (abstract or real)
        // Abstract folders use ID 0, real folders (FORMS, TEMPLATES) use their actual ID
        const folderId = row.isAbstractFolder ? 0 : row.id
        navigate(`/browser/${folderId}?type=${row.systemFolderType}`)
      } else {
        // Navigate to regular folder
        navigate(`/browser/${row.id}`)
      }
    } else {
      // Navigate to file details
      navigate(`/show-file/${row.id}`)
    }
  }

  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  const canShowMultiSelect = () => {
    return (
      access.isAllowed('documentsSelectMultiFiles') &&
      ((viewType as any) === SystemFolderType.REGULATED_CONTENT ||
        isPropertySearch ||
        ((viewType as any) === SystemFolderType.TEMPLATES &&
          fsi.uuid !== 'FOLDER_STRUCTURE' &&
          fsi.isFolderStructureSubFolder))
    )
  }

  const multiSelectionContext = useContext(TableMultiSelectionContext)
  const showMultiSelect = canShowMultiSelect() && multiSelectionContext
  const selectedIds = multiSelectionContext?.multiSelectState?.selectedIds || []
  const selectedWholeResult = multiSelectionContext?.multiSelectState?.selectedWholeResult || false
  const deselectAll = multiSelectionContext?.deselectAll

  // Load superseded documents for a specific FSI
  const getGroupedDocs = async (id: number, newExpandedRows: number[]) => {
    try {
      // Call API to load superseded documents using getFiles with subGroupEffectiveFsiId filter
      // Note: Using pageSize of 500 to handle documents with many superseded versions.
      // This is a reasonable limit as superseded documents are typically managed versions
      // of the same file, and having 500+ versions would be an exceptional edge case.
      // If needed, this could be enhanced with pagination in the future.
      const response = await context.api.files.getFiles(fsi?.id || 0, {
        subGroupEffectiveFsiId: id
      })
      setSupersededDocListByFsi((prev) => ({
        ...prev,
        [id]: response.elements || []
      }))
      setExpandedRows(newExpandedRows)
    } catch (err) {
      console.error('Failed loading Superseded Documents:', err)
      setSupersededDocListByFsi((prev) => ({
        ...prev,
        [id]: []
      }))
    }
  }

  // Toggle row expansion for superseded documents
  const toggleRow = (id: number) => {
    const currentIndex = expandedRows.indexOf(id)
    const newExpandedRows = [...expandedRows]
    if (currentIndex >= 0) {
      // Row is already expanded, so collapse it
      newExpandedRows.splice(currentIndex, 1)
      setExpandedRows(newExpandedRows)
    } else {
      // Row is not expanded, so expand it
      newExpandedRows.push(id)
      getGroupedDocs(id, newExpandedRows)
    }
  }

  const isRowExpanded = (id: number) => {
    return expandedRows.includes(id)
  }

  return (
    <div>
      <RcmTable
        className="chita-table fsiTable"
        onRowEnter={handleRowEnter}
        simplePaginate={isPropertySearch}
        noResultsText="NO_RESULTS_FILTERED"
        GridItemView={(props) => <FsiGridItem {...props} viewType={viewType} />}
        rowKey={viewType === DOCUMENTS_VIEW_TYPE.systemDocuments ? 'key' : 'id'}
        HeadRow={
          <TableHeadRow>
            <TableHeadCell className="pr5 w20px">
              {/* Expansion column for superseded documents */}
              <span>&nbsp;</span>
            </TableHeadCell>
            {showMultiSelect && (
              <th className="has-checkbox" id="multiselect-header">
                <SelectAllRows />
              </th>
            )}
            <TableHeadCell id="fb447770-d1c6-47cb-bac9-abc46fb2349f" sort={isSortingEnabled ? 'NAME' : undefined}>
              {translate('NAME')}
            </TableHeadCell>
            {isSiteNumberShown && (
              <TableHeadCell className="narrow" id="a930a8f2-7712-471f-95b4-3de0b7e6df32">
                {translate('TABLE_TH_STUDY_ENV_SITE_NO')}
              </TableHeadCell>
            )}
            {isDataPageShown && (
              <TableHeadCell className="narrow" id="31f502b1-b4e6-4da1-9f50-6ca31fadf9e4">
                {translate('TABLE_TH_DATAPAGE')}
              </TableHeadCell>
            )}
            {isSubjectShown && (
              <TableHeadCell className="narrow" id="c49feeff-f58b-4e4e-9e36-19e6694a45e5">
                {translate('TABLE_TH_SUBJECT')}
              </TableHeadCell>
            )}
            {!hideVersion && (
              <TableHeadCell
                className="narrow"
                id="1604015b-c256-4d0a-b7af-ce9370ca8edd"
                sort={isSortingEnabled ? 'VERSION' : undefined}
              >
                {translate('VERSION')}
              </TableHeadCell>
            )}
            {isPropertySearch && qcReviewEnabled && (
              <TableHeadCell className="narrow" id="00bdb0be-f95f-4834-9f83-53e82ca62k95">
                {translate('QC_REVIEW_STATUS_FILTER')}
              </TableHeadCell>
            )}
            <TableHeadCell
              className="narrow"
              id="0ebdb0be-f75f-4834-9f83-53e82ca62b95"
              sort={isSortingEnabled ? 'WORKFLOW' : undefined}
            >
              {translate('CHANGE_REQUEST')}
            </TableHeadCell>
            <TableHeadCell
              className="narrow"
              id="c1288a38-a5f0-4334-9f59-d0b5bd8eecb9"
              sort={isSortingEnabled ? 'UPDATED_ON' : undefined}
            >
              {translate('TABLE_TH_UPDATED_ON')}
            </TableHeadCell>
            <TableHeadCell id="4bbaf99c-1c90-46b2-a6ca-089351ce13ca" className="actions">
              {translate('ACTIONS')}
            </TableHeadCell>
          </TableHeadRow>
        }
        Row={(props) => {
          const row = props.row
          const isFolder = row.type === FsiType.FOLDER
          const isDisabledForSelection = row.workflow || !row.valid || isFolder

          return (
            <>
              <TableRow {...props}>
                {/* Expansion toggle for superseded documents */}
                {row?.supGroupEffectiveFsi ? (
                  <td width="20px" className="pr5">
                    <span
                      className={'select-folder-trigger cursor-pointer' + (isRowExpanded(row.id) ? ' active' : '')}
                      onClick={() => toggleRow(row.id)}
                    >
                      <i className={'fa fa-lg ' + (isRowExpanded(row.id) ? 'fa-angle-down' : 'fa-angle-right')} />
                    </span>
                  </td>
                ) : (
                  <td width="20px" className="pr5">
                    {/* Empty cell for alignment */}
                  </td>
                )}
                {showMultiSelect && (
                  <td className="has-checkbox">
                    {isFolder ? (
                      // Empty cell for folders
                      <span>&nbsp;</span>
                    ) : isDisabledForSelection ? (
                      // Disabled checkbox for documents in workflow or invalid documents
                      <div className="just-checkbox disabled">
                        <input type="checkbox" disabled={true} checked={false} readOnly />
                        <label>
                          <div className="chkbc"></div>
                        </label>
                      </div>
                    ) : (
                      // Active checkbox for selectable documents
                      <SelectRow row={row} disabled={false} />
                    )}
                  </td>
                )}
                <td className="name clipped-cell with-thumb">
                  <div className="d-flex align-left">
                    {row.type !== FsiType.FOLDER && <ValidDocIndicator fsi={row} />}
                    <DocIcon fsi={row} />
                    {shouldShowQualityIssueIndicator(fsi, row) && <WrapperOverlay row={row} />}
                    <div className="ellipsis" title={getFsiNameTooltip(row)}>
                      {row.type === FsiType.PLACEHOLDER ||
                      (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
                      !row.valid ? (
                        <span>
                          {row.name}
                          {isEtmfEnabled && row.type === FsiType.PLACEHOLDER && (
                            <DueDateIndicator dueDateStatus={row.dueDateStatus} translate={translate} />
                          )}
                        </span>
                      ) : row.isReport ? (
                        <TextLinkButton
                          action={() => {
                            window.open(`/api/files/${row.id}/download`, '_blank')
                          }}
                        >
                          {row.name}
                        </TextLinkButton>
                      ) : (
                        <a
                          href={
                            row.type === FsiType.FOLDER
                              ? `/browser/${row.id}?type=${row.systemFolderType}&page=1`
                              : `/show-file/${row.id}`
                          }
                          onClick={(e) => {
                            e.preventDefault()
                            handleRowEnter(row)
                          }}
                        >
                          {row.name}
                        </a>
                      )}
                    </div>
                    <DocState fsi={row} />
                  </div>
                </td>
                {isSiteNumberShown && (
                  <td className="narrow">
                    {row.supplementalData?.siteNumber || row.suppMetadataValue || translate('NOT_AVAILABLE')}
                  </td>
                )}
                {isDataPageShown && (
                  <td className="narrow">{row.supplementalData?.dataPageName || translate('NOT_AVAILABLE')}</td>
                )}
                {isSubjectShown && (
                  <td className="narrow">{row.supplementalData?.subjectName || translate('NOT_AVAILABLE')}</td>
                )}
                {!hideVersion && (
                  <td className="narrow">
                    <span title={row.fileVersionNo ? row.fileVersionNo : ''}>
                      {row.fileVersionNo ? row.fileVersionNo : translate('NOT_AVAILABLE')}
                    </span>
                  </td>
                )}
                {isPropertySearch && qcReviewEnabled && <td className="narrow">{row?.qcReviewStatus || '-'}</td>}
                <td className="narrow">
                  {!row.workflow ? (
                    translate('NOT_AVAILABLE')
                  ) : access.isAllowed('workflowsAccessDetail') ? (
                    <TextLinkButton
                      action={() => {
                        if (row.workflow.length === 1) {
                          showModal(<WorkflowDetailsModal wfId={row.workflow[0].id} onClose={() => {}} />)
                        } else {
                          showModal(<MultipleWorkflowsModal workflow={row.workflow} onClose={() => {}} />, {
                            keepParentModalsOpen: true
                          })
                        }
                      }}
                    >
                      {row.workflow.length === 1 ? row.workflow[0].formattedId : translate('WORKFLOW_MULTIPLE')}
                    </TextLinkButton>
                  ) : row.workflow.length === 1 ? (
                    row.workflow[0].formattedId
                  ) : (
                    translate('WORKFLOW_MULTIPLE')
                  )}
                </td>
                <td className="narrow updated">{formatDateTime(row.updatedOn)}</td>
                <td className="actions">
                  {row.valid &&
                    (row.isReport ? (
                      <ReportsTableMenu fsi={row} />
                    ) : (
                      <TableMultiSelectionConsumer>
                        {(tableMultiSelection) => {
                          const selectedItems = tableMultiSelection?.multiSelectState?.selectedIds
                            ? tableData.entries.filter((i: any) =>
                                tableMultiSelection?.multiSelectState?.selectedIds.includes(i.id)
                              )
                            : []

                          const options = {
                            ...(tableMultiSelection?.multiSelectState?.selectedIds ? { selectedItems } : null),
                            ...(tableMultiSelection?.multiSelectState || {}),
                            parentFolderId: fsi?.id,
                            isPropertiesSearch: isPropertySearch,
                            selectedIds: selectedIds.length > 0 ? selectedIds.map(Number) : undefined,
                            selectedWholeResult: selectedWholeResult,
                            deselectAll: deselectAll
                          }

                          return <DocumentActionMenu item={row} options={options} />
                        }}
                      </TableMultiSelectionConsumer>
                    ))}
                </td>
              </TableRow>

              {/* Render superseded documents if row is expanded */}
              {isRowExpanded(row.id) && supersededDocListByFsi[row.id]?.length
                ? supersededDocListByFsi[row.id].map((supersededRow: any) => (
                    <TableRow key={supersededRow.id} row={supersededRow} id="superseded-row">
                      <td width="20px" className="pr5">
                        {/* Empty cell for alignment */}
                      </td>
                      {showMultiSelect && (
                        <td className="has-checkbox">
                          {supersededRow.type !== FsiType.FOLDER && (
                            <SelectRow row={supersededRow} disabled={supersededRow.workflow || !supersededRow.valid} />
                          )}
                        </td>
                      )}
                      <td className="name clipped-cell with-thumb">
                        <div className="d-flex align-left">
                          {supersededRow.type !== FsiType.FOLDER && <ValidDocIndicator fsi={supersededRow} />}
                          <DocIcon fsi={supersededRow} />
                          {shouldShowQualityIssueIndicator(fsi, supersededRow) && (
                            <WrapperOverlay row={supersededRow} isSuperseded={true} />
                          )}
                          <div
                            className={`ellipsis ${shouldShowQualityIssueIndicator(fsi, supersededRow) ? '' : 'pl30'}`}
                            title={getFsiNameTooltip(supersededRow)}
                          >
                            {supersededRow.type === FsiType.PLACEHOLDER ||
                            (supersededRow.acl === AclLevel.BROWSE && supersededRow.type !== FsiType.FOLDER) ||
                            !supersededRow.valid ? (
                              <span>
                                {supersededRow.name}
                                {isEtmfEnabled && supersededRow.type === FsiType.PLACEHOLDER && (
                                  <DueDateIndicator dueDateStatus={supersededRow.dueDateStatus} translate={translate} />
                                )}
                              </span>
                            ) : supersededRow.isReport ? (
                              <TextLinkButton
                                action={() => {
                                  window.open(`/api/files/${supersededRow.id}/download`, '_blank')
                                }}
                              >
                                {supersededRow.name}
                              </TextLinkButton>
                            ) : (
                              <a
                                href={`/show-file/${supersededRow.id}`}
                                onClick={(e) => {
                                  e.preventDefault()
                                  navigate(`/show-file/${supersededRow.id}`)
                                }}
                              >
                                {supersededRow.name}
                              </a>
                            )}
                          </div>
                          <DocState fsi={supersededRow} />
                        </div>
                      </td>
                      {isSiteNumberShown && (
                        <td className="narrow">
                          {supersededRow.supplementalData?.siteNumber ||
                            supersededRow.suppMetadataValue ||
                            translate('NOT_AVAILABLE')}
                        </td>
                      )}
                      {isDataPageShown && (
                        <td className="narrow">
                          {supersededRow.supplementalData?.dataPageName || translate('NOT_AVAILABLE')}
                        </td>
                      )}
                      {isSubjectShown && (
                        <td className="narrow">
                          {supersededRow.supplementalData?.subjectName || translate('NOT_AVAILABLE')}
                        </td>
                      )}
                      {!hideVersion && (
                        <td className="narrow">
                          <span title={supersededRow.fileVersionNo || ''}>
                            {supersededRow.fileVersionNo || translate('NOT_AVAILABLE')}
                          </span>
                        </td>
                      )}
                      {isPropertySearch && qcReviewEnabled && (
                        <td className="narrow">{supersededRow?.qcReviewStatus || '-'}</td>
                      )}
                      <td className="narrow">
                        {!supersededRow.workflow ? (
                          translate('NOT_AVAILABLE')
                        ) : access.isAllowed('workflowsAccessDetail') ? (
                          <TextLinkButton
                            action={() => {
                              if (supersededRow.workflow.length === 1) {
                                showModal(
                                  <WorkflowDetailsModal wfId={supersededRow.workflow[0].id} onClose={() => {}} />
                                )
                              } else {
                                showModal(
                                  <MultipleWorkflowsModal workflow={supersededRow.workflow} onClose={() => {}} />,
                                  { keepParentModalsOpen: true }
                                )
                              }
                            }}
                          >
                            {supersededRow.workflow.length === 1
                              ? supersededRow.workflow[0].formattedId
                              : translate('WORKFLOW_MULTIPLE')}
                          </TextLinkButton>
                        ) : supersededRow.workflow.length === 1 ? (
                          supersededRow.workflow[0].formattedId
                        ) : (
                          translate('WORKFLOW_MULTIPLE')
                        )}
                      </td>
                      <td className="narrow updated">{formatDateTime(supersededRow.updatedOn)}</td>
                      <td className="actions">
                        {supersededRow.valid &&
                          (supersededRow.isReport ? (
                            <ReportsTableMenu fsi={supersededRow} />
                          ) : (
                            <TableMultiSelectionConsumer>
                              {(tableMultiSelection) => {
                                const selectedItems = tableMultiSelection?.multiSelectState?.selectedIds
                                  ? tableData.entries.filter((i: any) =>
                                      tableMultiSelection?.multiSelectState?.selectedIds.includes(i.id)
                                    )
                                  : []

                                const options = {
                                  ...(tableMultiSelection?.multiSelectState?.selectedIds ? { selectedItems } : null),
                                  ...(tableMultiSelection?.multiSelectState || {}),
                                  deselectAll: tableMultiSelection?.deselectAll,
                                  parentFolderId: fsi?.id,
                                  isPropertiesSearch: isPropertySearch,
                                  selectedIds: selectedIds.length > 0 ? selectedIds.map(Number) : undefined,
                                  selectedWholeResult: selectedWholeResult
                                }

                                return <DocumentActionMenu item={supersededRow} options={options} />
                              }}
                            </TableMultiSelectionConsumer>
                          ))}
                      </td>
                    </TableRow>
                  ))
                : null}
            </>
          )
        }}
      />
    </div>
  )
}
